"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const planningFeatures = [
    {
        title: "Rolling 12-Month Forecast",
        body: "We issue quarterly supply schedules 90 days in advance, aligned to your plant's production calendar and buffer stock targets."
    },
    {
        title: "Grade Certification",
        body: "Each consignment ships with a Certificate of Analysis from an accredited third-party assay house. Typical Sb grade: 99.50–99.85%."
    },
    {
        title: "Inventory Buffering",
        body: "We maintain bonded warehouse stock at Rotterdam, Antwerp, and Durban to protect against shipping delays."
    },
    {
        title: "Dedicated Account Management",
        body: "A single point of contact for all scheduling, documentation, and escalation — accessible on a 24/5 basis."
    }];


export default function SupplyPlanningSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="supply-planning-heading">

            <div
                className="px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center"
                style={{ maxWidth: "1280px", margin: "0 auto" }}>

                {/* Text */}
                <div className="flex flex-col gap-8">
                    <div className="reveal">
                        <span className="type-subheading">Supply Planning</span>
                    </div>
                    <h2
                        id="supply-planning-heading"
                        className="type-display-dark reveal reveal-delay-1">

                        Planned supply,
                        <br />
                        not reactive procurement.
                    </h2>
                    <p className="type-body-lg reveal reveal-delay-2" style={{ color: "rgba(255,255,255,0.38)" }}>
                        Our supply planning model is built around your production schedule,
                        not ours. We integrate directly with your procurement calendar to
                        ensure zero unplanned stockouts.
                    </p>

                    <div className="flex flex-col gap-6 reveal reveal-delay-3">
                        {planningFeatures?.map((f) =>
                            <div
                                key={f?.title}
                                className="flex gap-4 items-start"
                                style={{
                                    paddingBottom: "24px",
                                    borderBottom: "1px solid var(--color-border)"
                                }}>

                                <span
                                    aria-hidden="true"
                                    style={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        background: "var(--color-red)",
                                        marginTop: "8px",
                                        flexShrink: 0
                                    }} />

                                <div>
                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.9375rem",
                                            fontWeight: 600,
                                            color: "var(--color-white)",
                                            marginBottom: "4px"
                                        }}>

                                        {f?.title}
                                    </p>
                                    <p className="type-body" style={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.38)" }}>
                                        {f?.body}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Image */}
                <div className="reveal reveal-delay-2 img-offset-wrapper">
                    <div
                        className="overflow-hidden"
                        style={{ borderRadius: "4px", aspectRatio: "4/5" }}>

                        <AppImage
                            src="https://img.rocket.new/generatedImages/rocket_gen_img_13d7e05ba-1768082738298.png"
                            alt="Industrial warehouse with bulk material storage and logistics infrastructure"
                            fill
                            className="object-cover" />

                    </div>
                </div>
            </div>
        </section>);

}